import type {
  EarnConfigPayload,
  EarnConfigRow,
} from '@/redux/thunks/achievementThunks';

/**
 * Look up the earn-config row for an achievement type.
 * Returns null if the type isn't enabled in the catalog.
 */
export function findEarnRow(
  cfg: EarnConfigPayload | null | undefined,
  achievementType: string,
): EarnConfigRow | null {
  if (!cfg) return null;
  return cfg.rows.find((r) => r.achievementType === achievementType) ?? null;
}

/**
 * Format the display amount for an achievement.
 *   fixed_klv              → "+N KLV"          (multiplier applied)
 *   pct_of_fee_from_trigger_tx → "+N% of fee"   (percentage of trigger-tx fee)
 *   pct_of_accumulated_fees    → "+N% of fees"
 * Returns null when there's no earn row for the type (locked payout or
 * disabled in config).
 */
export function formatEarnAmount(
  cfg: EarnConfigPayload | null | undefined,
  achievementType: string,
): string | null {
  const row = findEarnRow(cfg, achievementType);
  if (!row || !cfg) return null;
  if (row.payoutType === 'fixed_klv') {
    const klv = row.payoutValue * cfg.multiplier;
    if (klv <= 0) return null;
    const rounded = klv >= 10 ? Math.round(klv).toString() : klv.toFixed(klv < 1 ? 2 : 1).replace(/\.0$/, '');
    return `+${rounded} KLV`;
  }
  if (row.payoutType === 'pct_of_fee_from_trigger_tx') {
    return `+${row.payoutValue}% of fee`;
  }
  if (row.payoutType === 'pct_of_accumulated_fees') {
    return `+${row.payoutValue}% of fees`;
  }
  return null;
}

/**
 * Sum of all `fixed_klv` payouts (with multiplier) in the enabled
 * catalog — "earn up to N KLV across the full catalog" for marketing
 * callouts. pct_* rows aren't counted (unknown trigger fee).
 */
export function sumFixedCatalogKlv(cfg: EarnConfigPayload | null | undefined): number {
  if (!cfg) return 0;
  return cfg.rows
    .filter((r) => r.payoutType === 'fixed_klv')
    .reduce((acc, r) => acc + r.payoutValue * cfg.multiplier, 0);
}
